
/**
 * @description Enum per lo stato di un Report, Componente o Elemento.
 */
export type ReportStato = 'empty' | 'generated' | 'edited' | 'approved' | 'drafted' | 'completed';
export type ComponentStato = 'empty' | 'generated' | 'edited' | 'approved' | 'drafted' | 'completed';
export type ElementStato = 'empty' | 'generated' | 'edited' | 'approved' | 'drafted' | 'completed';
export type ValidationStatus = "not_required" | "required"| "validated";

export type ReportStatus =
  | 'completato'
  | 'da_completare'
  | 'Validato'
  | 'Da Validare'
  | 'generato'
  | 'da_generare';

  
/**
 * @description Enum per il tipo di sorgente di un Elemento.
 * Nuovi valori: 'parent', 'table', 'image'.
 */
export type SourceType = 'parent' | 'table' | 'image';

/**
 * @description Rappresenta una sorgente di dati per un elemento.
 * Modificati i campi: aggiunto 'name', 'source_ref', rimosso 'content'.
 */
export interface SourceRead {
  id: string; // Unique identifier for this object (UUID)
  created_at: string; // The date and time the object was created (date-time)
  updated_at?: string | null; // The date and time the object was updated (date-time or null)
  name: string; // Source name
  source_type: SourceType; // Source type
  source_ref: string; // Source reference
  element_id: string; // Element ID (UUID)
}

export interface SourceDetail {
  id: string;
  content: string;
}

export type Metric = {
  title: string;
  value: number;
  task_type?: "be_strategy" | "advanced_features" | "standard";

};

export type IndicatoreItem = {
  title: string;
  metrics: Metric[];
};


/**
 * @description Rappresenta un elemento di un componente.
 * Rimosso 'element_template_id'.
 */
export interface ElementRead {
  id: string; // Unique identifier for this object (UUID)
  created_at: string; // The date and time the object was created (date-time)
  updated_at?: string | null; // The date and time the object was updated (date-time or null)
  name: string; // Element name (maxLength: 255)
  value: any | null; // Value of the element (can be object, string, number, etc.)
  validation: ValidationStatus; // Validated value (default: false)
  component_id: string; // Component ID (UUID)
  // element_template_id: string; // Rimosso secondo la nuova OpenAPI
  status: ElementStato; // status of the element
  report_status: ReportStato
  sources: SourceRead[]; // List of sources related to this element
  score: BaseScore;

}

/**
 * @description Payload per l'aggiornamento di un elemento.
 */
export interface ElementUpdate {
  value?: any | null;
  validated?: ValidationStatus;
  summary_selected_information?: string[];
  reasoning?: string[];
  list_relevant_sentences?: string[];
  metrics?: Metric[];
} 



export type BankShare = {
  name: string;
  share: number;
};

export type MarketData = {
  market_name: string;
  market_traded_value: number;
  banks: BankShare[];
};

export type ElementUpdateIndicePenConValue = {
  value: {
    raw_data: MarketData[];
  };
};

/**
 * @description Risposta per l'aggiornamento di un elemento.
 * Nuovo tipo introdotto dall'API. Simile a ElementRead ma senza 'sources'.
 */
export interface ElementUpdateResponse {
  id: string; // Unique identifier for this object (UUID)
  created_at: string; // The date and time the object was created (date-time)
  updated_at?: string | null; // The date and time the object was updated (date-time or null)
  name: string; // Element name (maxLength: 255)
  value: any | null; // Value of the element (can be object, string, number, etc.)
  validated: ValidationStatus // Validated value (default: false)
  component_id: string; // Component ID (UUID)
  status: ElementStato; // status of the element
}

/**
 * @description Rappresenta un componente di un report.
 */
export interface ComponentRead {
  id: string; // Unique identifier for this object (UUID)
  created_at: string; // The date and time the object was created (date-time)
  updated_at?: string | null; // The date and time the object was updated (date-time or null)
  kind: string; // Component kind (es. "Driver01", "MarketAccessItaly", ...)
  name: string; // Component name (maxLength: 255)
  validation:ValidationStatus // Validated component (default: false)
  report_id: string; // Report ID (UUID)
  component_template_id: string; // Component template ID (UUID)
  status: ComponentStato; // status of component
  report_status: ReportStato;
  elements: ElementRead[]; // List of elements
}

/**
 * @description Rappresenta un template di componente.
 * Nuovo tipo introdotto dall'API.
 */
export interface ComponentTemplateRead {
  id: string; // Unique identifier for this object
  created_at: string; // The date and time the object was created
  updated_at?: string | null; // The date and time the object was updated
  kind: string; // Template kind
  component_list: string[]; // List of components (names or IDs?) - assuming string for now
  element_schema: Record<string, any>; // The JSON Schema for element values
  element_list: string[]; // List of elements (names or IDs?) - assuming string for now
}

/**
 * @description Rappresenta un template di report.
 * Nuovo tipo introdotto dall'API.
 */
export interface ReportTemplateRead {
  id: string; // Unique identifier for this object
  created_at: string; // The date and time the object was created
  updated_at?: string | null; // The date and time the object was updated
  kind: string; // Template kind
  description: string; // Template description
  component_templates: ComponentTemplateRead[]; // List of component templates
}

/**
 * @description Rappresenta un report completo letto dall'API.
 * Il campo 'title' è stato rinominato in 'name' nell'API.
 */
export interface Reports {
  id: string; // Unique identifier for this object (UUID)
  created_at: string; // The date and time the object was created (date-time)
  updated_at?: string | null; // The date and time the object was updated (date-time or null)
  name: string; // Report name (maxLength: 255) - Ex 'title'
  description?: string | null; // Report description (maxLength: 255 or null)
  report_template_id: string; // Report template ID (UUID)
  status: ReportStato; // status of report
  components: ComponentRead[]; // List of components

  // Campi aggiunti per compatibilità con il tuo codice esistente,
  // che potrebbero usare 'nome', 'dataCreazione', 'stato'.
  // Questi verranno mappati nelle funzioni del servizio API.
  nome: string; // Mappa a 'name'
  dataCreazione: string; // Mappa a 'created_at'
  stato: ReportStato; // Mappa a 'status'
}

/**
 * @description Read schema specializzato restituito da GET /api/v1/reports/best_execution.
 * `bank_names` è derivato server-side dagli elementi MarketAccessItaly.
 */
export interface BestExecutionReport {
  id: string;
  created_at: string;
  updated_at?: string | null;
  name: string;
  description?: string | null;
  report_template_id: string;
  /** Anno di riferimento del report. Null sui report creati prima della release di hardening. */
  reference_year?: number | null;
  status: ReportStato;
  owner?: string | null;
  kind: string;
  bank_names: string[];
}

/**
 * @description Payload per la creazione di un nuovo report.
 * Il campo 'title' è stato rinominato in 'name'.
 */

export type Report = {
  id: string;
  nome: string;
  dataCreazione: string;
  stato: ReportStato;
  descrizione: string;
};

export interface ReportCreate {
  name: string; // Report name (maxLength: 255) - Ex 'title'
  description?: string | null; // Report description (maxLength: 255 or null)
  bank_names: string[];
  /**
   * Obbligatorio dalla release di hardening: deve essere l'anno corrente o il precedente,
   * altrimenti il BE risponde 422. Lo legge solo il driver penetrazione mercati.
   */
  reference_year: number;
}

/**
 * @description Payload per l'aggiornamento di un report.
 * Il campo 'title' è stato rinominato in 'name'.
 */
export interface ReportUpdate {
  name?: string | null; // Report name (maxLength: 255) - Ex 'title'
  description?: string | null; // Report description (maxLength: 255 or null)
}

/**
 * @description Rappresenta un errore di validazione HTTP.
 */
export interface ValidationError {
  loc: Array<string | number>; // Location of the error
  msg: string; // Error message
  type: string; // Error type
}

/**
 * @description Rappresenta una risposta di errore HTTP.
 */
export interface HTTPValidationError {
  detail: ValidationError[]; // List of validation errors
}

/**
 * @description Tipo per l'oggetto Intermediario, basato sul log fornito.
 * Questo tipo non è presente nell'openapi.json, ma è stato dedotto dal tuo utilizzo.
 */
export interface Intermediario {
  id: string;
  nome: string;
  mercato: string[];
  stato: string;
  fonti: Array<{
    titolo: string;
    contenuto: string;
    fonteOriginale: string;
  }>;
}

/**
 * @description Props per il componente ContainerListaReportPage.
 */
export interface ContainerListaReportPageProps {
  reports: Reports[];
}

// Tipo per ciascuna banca all'interno di un mercato
export type QuotaBanca  = {
  name: string;
  share: number; // Percentuale di share (es. 18.64)
  detected_name : string; 
  selected : boolean;
};

// Tipo per ciascun mercato in raw_data
export type DatiMercato  = {
  market_name: string;
  market_traded_value: number;
  banks: QuotaBanca [];
};

// Tipo per i valori scambiati per banca
export type ValoreScambiatoPerBanca  = {
  name: string;
  value: number;
  percentage_of_total: number;
  score : BaseScore;
};

// Tipo per i valori scambiati per mercato
export type ValoreScambiatoPerMercato  = {
  name: string;
  value: number;
  percentage_of_total: number;
  score : BaseScore;

};

// Tipo per traded_values
export type ValoriScambiati  = {
  by_bank: ValoreScambiatoPerBanca [];
  by_market: ValoreScambiatoPerMercato [];
};

// Tipo principale per indicePenetrazioneCash
export type IndicePenetrazioneCash = {
  id: string; 
  name: string;
  status:ElementStato,
  report_status: ReportStato;
  validation: ValidationStatus;
  raw_data: DatiMercato [];
  traded_values: ValoriScambiati ;
  score: BaseScore;
};


export type Fonte = {
  titolo: string;
  contenuto: string;
  fonteOriginale: string;
};



export type Indicatore = {
  title: string;
  metrics: Metric[];
};

export interface IntermediarioAdesioneMercatiItem {
  id: number;
  nome: string;
  areaGeografica: string;
  sedeEsecuzione: string;
  tipologiaSede: string;
}


export interface IntermediarioAdesioneMercati {
  id: number;
  nome: string;
  totaleMercati: number;
  azioni: IntermediarioAdesioneMercatiItem[];
  obbligazioni: IntermediarioAdesioneMercatiItem[];
  derivati: IntermediarioAdesioneMercatiItem[];
  altriMercati: IntermediarioAdesioneMercatiItem[];
  riassunto: string[];
  frasiRilevanti: string[];
  ragionamento: string[];
  stato: any;
  fonti: Fonte[];
  indicatore?: Indicatore[];
}

export type Venue = {
id?: string;          
  mic: string;
  operating_mic: string;
  market_name: string;
  legal_entity_name: string;
  acronym: string;
  status: string;
  validated?: boolean;
  CountryCode: string;
  City: string;
  MarketCategory: string;
};

export type AmbiguousVenue = Venue[];
export type TipoMercatoKey = 'equity' | 'bonds' | 'derivatives' | 'others'| 'undefined';

export type AdesioneMercatiTipo = {
  counter: number;
  warning: boolean;
  venues: Venue[];
  ambiguous_venues: AmbiguousVenue[];
  unknown_venues: string[];
};

export type AdesioneMercati = {
  equity?: AdesioneMercatiTipo;
  bonds?: AdesioneMercatiTipo;
  derivatives?: AdesioneMercatiTipo;
  others?: AdesioneMercatiTipo;
  undefined?: AdesioneMercatiTipo;
  score: BaseScore;

};

export type Mercato = {
  id: string;
  name: string;
  validation: ValidationStatus;
  status: ReportStato;  // riusa il tuo tipo ReportStatus
  report_status : ReportStato;
  value: AdesioneMercati;
  score: BaseScore;

};

export type ModalData = 
  | { type: 'mercato', data: { tipoMercato: string; lista: Venue[]; isAmbiguous: boolean; } }
  | { type: 'unknown', data: { tipoMercato: string; lista: string[]; } }
  | { type: 'mercatiGlobali', data: { tipoMercato: string; lista: Venue[]; } }
  | null;

  export type BaseScore = {
    total_score: number;
    score: number;
  };
  
  export type MarketMakerValue = {
    market_maker: boolean;
    equity: boolean;
    bonds: boolean;
    etf: boolean;
    etc_etn: boolean;
    warrants: boolean;
    covered_warrants: boolean;
    derivatives: boolean;
    score: BaseScore;
  };
  
    export type MarketMakerValueUpload = {
    equity: boolean;
    bonds: boolean;
    etf: boolean;
    etc_etn: boolean;
    warrants: boolean;
    covered_warrants: boolean;
    derivatives: boolean;
  };

  export type MarketMaker = {
    id: string;
    created_at: string;
    updated_at: string;
    name: string;
    status: ReportStato;        // "generated", ecc.
    report_status: ReportStato;
    validation: ValidationStatus;    // "not_required", ecc.
    component_id: string;
    value: MarketMakerValue;
  };
  
  export type MarketMakerUpdatePayload = {
    value: MarketMakerValueUpload;
};
  export type TransparencyClass =
  | "strategy_with_fee_breakdown"
  | "strategy_with_total_costs"
  | "strategy_without_costs"
  | "no_strategy";

export type TransparencyScore = {
  score: number;
  bonus: number;
  malus: number;
  total_score: number;
};

export type InformationTransparencyValue = {
  score: TransparencyScore;
  /**
   * Data di aggiornamento del documento, ISO es. "2025-08-28".
   * Null quando l'estrazione non l'ha trovata nel documento: non significa
   * che il documento non ne abbia una.
   */
  doc_update_date: string | null;
  transparency_class: TransparencyClass;
  summary_selected_information: string;
  list_relevant_sentences: string[];
  reasoning: string;
};
export type InformationTransparencyValueUpload = {
  transparency_class: TransparencyClass;
};

export type InformationTransparency = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  status: ReportStato;       // "generated", ecc.
  report_status: ReportStato;
  validation: ValidationStatus;    // "not_required", ecc.
  component_id: string;
  value: InformationTransparencyValue;
};

export type  InformationTransparencyUpdatePayload = {
  value: InformationTransparencyValueUpload;
};

// struttura dello score
export type ReclamoScore = {
  score: number;
  bonus: number;
  malus: number;
  total_score: number;
};

// struttura del value
export type ReclamoValue = {
  total_claims: number;
  investment_services_claims: number;
  percentage_investment_claims: number;
  score: ReclamoScore;
  source_fiscal_year : number;
};

export type ReclamoValueUpdate = {
  total_claims: number;
  investment_services_claims: number;
  source_fiscal_year : number;
};

// struttura completa del reclamo
export type Reclamo = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  status: ReportStato;
  report_status: ReportStato;
  validation: ValidationStatus;
  component_id: string;
  value: ReclamoValue;
};

export type ReclamoUpdatePayload = {
  value: ReclamoValueUpdate;
};

// lista di reclami
export type ReclamoList = Reclamo[];

// Tipi base per i punteggi
export type TrasparenzaESGSingleScore = {
  score: number;
  bonus: number;
  malus: number;
  total_score: number;
};

// Struttura interna di "gar_score"
export type TrasparenzaESGGarScore = {
  gar_turnover_percent: number;
  gar_capex_percent: number;
  list_relevant_sentences: string[];
  score: TrasparenzaESGSingleScore;
};

// Struttura interna di "rating_score"
export type TrasparenzaESGRatingScore = {
  rating: number | null;
};

// Struttura principale del campo "value"
export type TrasparenzaESGValue = {
  gar_score: TrasparenzaESGGarScore;
  rating_score: TrasparenzaESGRatingScore;
  score: TrasparenzaESGSingleScore;
};

// Oggetto completo
export type TrasparenzaESG = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  status: ReportStato;
  report_status: ReportStato;
  validation: ValidationStatus;
  component_id: string;
  value: TrasparenzaESGValue;
};

// Lista di elementi
export type TrasparenzaESGList = TrasparenzaESG[];

export type TrasparenzaESGUpdatePayload = {
  value: {
    gar_score: Pick<TrasparenzaESGGarScore, 'gar_turnover_percent' | 'gar_capex_percent'>;
    rating_score: TrasparenzaESGRatingScore;
  };
}


export type IndiceSoliditaScore = {
  score: number;
  bonus_malus: number;
  total_score: number;
};

export type IndiceSoliditaInference = {
  is_inferred_via_equivalence: boolean;
  explanation: string | null;
};

export type IndiceSoliditaValue = {
  status: string;
  value_percent: number;
  basis: string;
  scope: string;
  as_of_date: string;
  list_relevant_sentences: string[];
  inference: IndiceSoliditaInference;
  score: IndiceSoliditaScore;
};

export type IndiceSoliditaValueUpload = {
  value_percent: number;
};

export type IndiceSolidita = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  status: ReportStato;
  report_status: ReportStato;
  validation: ValidationStatus;
  component_id: string;
  value: IndiceSoliditaValue;
};

export type IndiceSoliditapdatePayload = {
  value: IndiceSoliditaValueUpload;
};

export type BankScores = {
  [key: string]: number;
};

export type DriverScores = {
  "Adesione mercati - Italia": BankScores;
  "Adesione mercati - Estero": BankScores;
  "Strategia di Best Execution - Mercati Obbligazionari": BankScores;
  "Strategia di Best Execution - Mercati Azionari": BankScores;
  "Strategia di Best Execution - Estero": BankScores;
  "Strategia di Best Execution - Italia": BankScores;
  "Indici di penetrazione mercati - Derivati": BankScores;
  "Indici di penetrazione mercati - Cash": BankScores;
  "Trasparenza Informativa": BankScores;
  "Indice di solidità": BankScores;
  "Reclami": BankScores;
  "Market Maker": BankScores;
  "Trasparenza ESG": BankScores;
};

// types.ts
export type ScenarioResult = {
  value: number;
  position: number;
};

// Ogni intermediario ha tutti gli scenari
export type ScenarioScores = Record<
  string, // intermediario
  Record<
    | "Scenario 1 INTERNAZIONALE HIGH"
    | "Scenario 2 INTERNAZIONALE MID"
    | "Scenario 3 INTERNAZIONALE LOW"
    | "Scenario 4 BED ALGORITMO"
    | "Scenario 5 BED FINANCIAL INSTRUMENTS"
    | "Scenario 6 BED MARKETS"
    | "Scenario 7 EQUILIBRATO"
    | "Scenario 8 TRANSPARENCY SOLIDITÀ"
    | "Scenario 9 TRANSPARENCY PENETRAZIONE"
    | "Scenario 10 TRANSPARENCY RECLAMI"
    | "Scenario 11 NAZIONALE LOW"
    | "Scenario 12 NAZIONALE MID"
    | "Scenario 13 NAZIONALE HIGH",
    ScenarioResult
  >
>;

export interface ScenarioRow {
  intermediario: string;
  scenari: number[];
}


export type BankScenarioData = {
  [key: string]: ScenarioScores;
};

export type Ranking = {
  "Classifica per punteggio totale di tutti gli scenari": number;
  "Classifica per punteggio totale senza scenario": number;
  "Classifica per posizione su ogni scenario": number;
  "Classifica per numero di presenze nella prima fascia di eccellenza": number;
  "Classifica finale": number;
};

export type BankRankings = {
  [key: string]: Ranking;
};


export type ScenarioWeightsByBank = {
  [bankName: string]: number;
};

export type AllScenarioWeights = {
  "Adesione mercati - Italia": ScenarioWeightsByBank;
  "Adesione mercati - Estero": ScenarioWeightsByBank;
  "Strategia di Best Execution - Mercati Obbligazionari": ScenarioWeightsByBank;
  "Strategia di Best Execution - Mercati Azionari": ScenarioWeightsByBank;
  "Strategia di Best Execution - Estero": ScenarioWeightsByBank;
  "Strategia di Best Execution - Italia": ScenarioWeightsByBank;
  "Indici di penetrazione mercati - Derivati": ScenarioWeightsByBank;
  "Indici di penetrazione mercati - Cash": ScenarioWeightsByBank;
  "Trasparenza Informativa": ScenarioWeightsByBank;
  "Indice di solidità": ScenarioWeightsByBank;
  "Reclami": ScenarioWeightsByBank;
  "Market Maker": ScenarioWeightsByBank;
  "Trasparenza ESG": ScenarioWeightsByBank;
};

export interface EventSearchPayload {
  report_id?: string;
  user_id?: string;
  event_type?: string;
  element_id?: string;
}
export interface Bank {
  id: string; 
  created_at: string;
  updated_at: string | null; 
  name: string; 
  website: string | null; 
  aliases: string[]; 
}

/** Tipi documento specifici di una banca (richiedono `bank_id`). */
export type BeBankDocumentType =
  | 'best_execution_strategy'
  | 'execution_venues'
  | 'financial_statements'
  | 'pillar_iii'
  | 'non_financial_statements'
  | 'claims';

/** Tipi documento generali (NON devono avere `bank_id`). */
export type BeGeneralDocumentType =
  | 'market_makers_borsa_italiana'
  | 'market_makers_other_exchanges'
  | 'amf_italy_report';

export type BeDocumentType = BeBankDocumentType | BeGeneralDocumentType;

export const BE_BANK_DOCUMENT_TYPES: BeBankDocumentType[] = [
  'best_execution_strategy',
  'execution_venues',
  'financial_statements',
  'pillar_iii',
  'non_financial_statements',
  'claims',
];

export const BE_GENERAL_DOCUMENT_TYPES: BeGeneralDocumentType[] = [
  'market_makers_borsa_italiana',
  'market_makers_other_exchanges',
  'amf_italy_report',
];

export const isGeneralDocumentType = (t: string): t is BeGeneralDocumentType =>
  (BE_GENERAL_DOCUMENT_TYPES as string[]).includes(t);

// 🔹 Tipo per una singola Document Class
export interface DocumentClass {
  id: string;
  created_at: string;
  updated_at?: string | null;
  /** Null per le classi generali (non legate a una banca). */
  bank_id?: string | null;
  document_type: BeDocumentType | string; // fallback in caso di nuovi tipi
  description?: string | null;
  is_required?: boolean;
  allows_multiple?: boolean;
  bank: null | Record<string, unknown>; // per compatibilità futura
}

// 🔹 Tipo per la risposta dell’inizializzazione
export type DocumentClassList = DocumentClass[];


export type SourceOriginKind = 'remote_uri' | 'upload';
export type IndexingStatus = 'pending' | 'indexing' | 'success' | 'error';

/** Corrisponde a `SourceCatalog` nell'OpenAPI. */
export interface DocumentSource {
  id: string;
  created_at: string;
  updated_at?: string | null;
  uri: string;
  hash: string;
  /** Come e' entrata la fonte: URL remoto o file caricato. */
  origin_kind?: SourceOriginKind;
  object_store_uri?: string | null;
  /** Nome file originale: da usare ovunque la UI mostri una fonte. */
  filename?: string;
  /** Id della versione sostituita, se questa fonte nasce da uno `switch`. */
  supersedes_id?: string | null;
  fiscal_year?: string | null;
  fiscal_year_evidence?: string | null;
  reporting_period_start?: string | null;
  reporting_period_end?: string | null;
  reporting_period_evidence?: string | null;
  /**
   * Rispecchia solo l'ultima run di un qualsiasi tipo documento:
   * NON derivarci la readiness BE, per quella serve /validate.
   */
  indexing_status?: IndexingStatus;
  indexing_error?: string | null;
  source_mimetype?: string | null;
  file_size?: number | null;
  parser_output_uri?: string | null;
}

export interface DocumentClassSource {
  id: string;
  created_at: string;
  updated_at: string;
  document_class_id: string;
  source_catalog_id: string;
  is_active: boolean;
  source: DocumentSource;
}

export interface CreateSourceParams {
  uri: string
  source_mimetype: string
}

export interface LinkSourceToDocumentClassParams {
  document_class_id: string
  source_catalog_id: string
  is_active: boolean
}

export interface ValidationResult {
  [bankName: string]: any;
  status: 'OK' | 'NOT OK';
}

export interface IngestRequest {
  bank_names: string[];
  /**
   * Rilancia ogni fonte delle banche indicate, incluse quelle gia' indicizzate.
   * E' anche l'unico modo per recuperare una run mai tornata indietro (>6h di default).
   */
  force?: boolean;
}

export interface PipelineRunInfo {
  id: string;
  pipeline_type: string;
  airflow_dag_id: string;
  airflow_dag_run_id: string;
  status: string;
  source_catalog_id?: string | null;
}

/**
 * I contatori sono in *unita'*: una fonte indicizzata per un tipo documento.
 * Una fonte collegata a due tipi vale 2.
 */
export interface IngestionResponse {
  message: string;
  triggered_pipelines: PipelineRunInfo[];
  /** Unita' trovate. */
  total_sources: number;
  /** Unita' saltate, per qualunque motivo. */
  skipped_sources: number;
  /** Un'ingestion di quell'unita' e' gia' in corso. */
  skipped_in_flight?: number;
  /** Fonte legacy senza bytes salvati: serve il backfill lato operatore. */
  skipped_no_snapshot?: number;
  /** Con `force`: run perse che sono state chiuse e rilanciate. */
  abandoned_stale?: number;
}

export interface DocumentClassValidation {
  actions: string[];
  errors: string[];
  info: string[];
}
export interface BankValidation {
  status: "OK" | "NOT OK";
  document_classes: Record<string, DocumentClassValidation>;
}

 export interface ValidationResultReport {
  status: "OK" | "NOT OK";
  general: BankValidation;
  banks: Record<string, BankValidation>;
}

// ---------------------------------------------------------------------------
// Freschezza fonti: check-sources / resolve / storico  (release di hardening)
// ---------------------------------------------------------------------------

/** Body di POST /reports/best_execution/check-sources */
export interface SourceCheckRequest {
  bank_names: string[];
}

/**
 * Esito dell'accodamento del check. Gli array contengono id di sorgenti.
 * Il check non registra nulla: gli outcome arrivano da /validate e dallo storico.
 */
export interface SourceCheckResponse {
  message: string;
  /** Sorgenti che vengono ri-scaricate adesso. */
  queued: string[];
  /** File caricati a mano: non vengono mai ri-scaricati. */
  skipped_pinned?: string[];
  /** Sorgenti HTML controllate da meno di 30 giorni. */
  skipped_within_ttl?: string[];
  /** Check gia' in corso, su una qualsiasi replica dell'API. */
  skipped_in_flight?: string[];
  /** Sorgenti attive distinte nello scope. */
  total_sources: number;
}

export type SourceCheckOutcome =
  | 'started'
  | 'unchanged'
  | 'changed'
  | 'unreachable'
  | 'acknowledged'
  | 'switched';

/** Un evento nello storico dei check di una fonte. */
export interface SourceCheck {
  id: string;
  source_catalog_id: string;
  checked_at: string;
  outcome: SourceCheckOutcome;
  observed_hash?: string | null;
  /** Valorizzato quando l'outcome e' `switched`: la nuova versione. */
  new_source_catalog_id?: string | null;
  error?: string | null;
}

export type IngestionStatus = 'pending' | 'running' | 'success' | 'error' | 'skipped';

/** Un evento nello storico delle ingestion di una fonte (uno per tipo documento). */
export interface SourceIngestion {
  id: string;
  source_catalog_id: string;
  document_type: string;
  ingestion_fingerprint?: string | null;
  status: IngestionStatus;
  pipeline_run_id?: string | null;
  collections?: Array<Record<string, unknown>> | null;
  parser_output_uri?: string | null;
  error_message?: string | null;
  /** Con status `skipped`: la run precedente identica che l'ha resa inutile. */
  skipped_for_ingestion_id?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
}

export type SourceResolveDecision = 'keep' | 'switch';

export interface SourceResolveRequest {
  decision: SourceResolveDecision;
  /** Solo con `switch`, e solo se il documento si e' spostato a un altro URL. */
  uri?: string | null;
}

export interface ResolvedDocumentClass {
  document_class_id: string;
  document_type: BeDocumentType | string;
  bank_name?: string | null;
}

export interface SourceResolveResponse {
  decision: SourceResolveDecision;
  /** La fonte su cui e' stata presa la decisione. */
  source: DocumentSource;
  /** La fonte che la sostituisce, o null quando l'origine non era davvero cambiata. */
  new_source?: DocumentSource | null;
  /** Quanti link attivi sono stati spostati sulla nuova fonte, su tutte le banche. */
  moved_links?: number;
  /** Classi che dopo lo switch vanno reindicizzate. */
  needs_ingestion?: ResolvedDocumentClass[];
  event: SourceCheck;
}

export type GenerationStatus = 'claimed' | 'computed' | 'reused' | 'failed';

/** Come il valore di un elemento e' arrivato a essere quello che e'. */
export interface ElementGeneration {
  id: string;
  element_id: string;
  fingerprint: string;
  inputs_fingerprint?: string | null;
  processing_fingerprint?: string | null;
  status: GenerationStatus;
  /** Con status `reused`: la generazione da cui il valore e' stato ripreso. */
  reused_from_id?: string | null;
  pipeline_run_id?: string | null;
  /** Falso quando l'utente aveva gia' modificato l'elemento a mano. */
  applied?: boolean | null;
  error?: string | null;
  completed_at?: string | null;
  created_at: string;
}

/**
 * Parametri di POST /reports/best_execution/sources (multipart).
 * Sostituisce la coppia POST /sources + POST /documents: registra i bytes
 * nel layout per banca e crea il link in una sola chiamata.
 */
export interface RegisterBeSourceParams {
  document_type: BeDocumentType | string;
  /** Obbligatorio per i tipi bank-specific, deve mancare per i tipi generali. */
  bank_id?: string | null;
  /** Esattamente uno tra `uri` e `file`. */
  uri?: string | null;
  file?: File | null;
  /** Hint opzionale: se contraddice il tipo rilevato il BE risponde 400. */
  source_mimetype?: string | null;
}